import * as Ariakit from '@ariakit/react'
import groupBy from 'lodash-es/groupBy'
import upperFirst from 'lodash-es/upperFirst'
import * as React from 'react'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import {
  EMBROIDERY_LOCATIONS,
  fromPlacementId,
} from '../../../../../containers/EmbroideryContainer/combination-rules/placementIds'
import { useEmbroideryGarment } from '../../../../../containers/EmbroideryContainer/providers/EmbroideryGarmentProvider'
import { useEmbroideryPrices } from '../../../../../containers/EmbroideryContainer/providers/EmbroideryPricingProvider'
import { useEmbroiderySelection } from '../../../../../containers/EmbroideryContainer/providers/EmbroiderySelectionProvider'
import { EmbroideryItemOfType } from '../../../../../containers/EmbroideryContainer/providers/types'
import {
  EmbroideryIcon,
  EmbroideryIconGroup,
} from '../../../../../containers/EmbroideryContainer/useEmbroideryDesigns'

import {
  Container,
  DisclosureCarat,
  DisclosureHeader,
  FinalSale,
  LineItem,
  LineItems,
  Rule,
  SummaryLabel,
  Total,
  TotalAnnouncement,
  TotalPrice,
} from './styles'

export type PriceSummaryProps = {
  productPriceInCents?: number
}

type PricedLine = { key: string; label: string; inCents: number | undefined }

type IconItemWithIcon = EmbroideryItemOfType<'icon'> & { values: { icon: EmbroideryIcon } }

const iconGroupLabel = (icon: EmbroideryIcon, iconGroups: readonly EmbroideryIconGroup[]): string =>
  iconGroups.find(group => group.icons.some(candidate => candidate.id === icon.id))?.displayName ??
  icon.image.label

export const PriceSummary: React.FC<PriceSummaryProps> = ({ productPriceInCents }) => {
  const { itemsEnabledForBag } = useEmbroiderySelection()
  const {
    designs: { iconGroups },
  } = useEmbroideryGarment()
  const {
    formatPrice,
    piecesInCentsForItems,
    setupCountForItems,
    setupInCentsForItems,
    totalInCentsForItems,
  } = useEmbroideryPrices()
  const {
    embroidery: {
      modal: {
        priceSummary: { label, lineItems, finalSaleLabel, totalLabel },
      },
    },
  } = useTranslation()

  const setupCount = setupCountForItems(itemsEnabledForBag)
  const setupInCents = setupInCentsForItems(itemsEnabledForBag)
  const embroideryInCents = totalInCentsForItems(itemsEnabledForBag)
  const totalInCents =
    embroideryInCents === undefined ? undefined : (productPriceInCents ?? 0) + embroideryInCents

  const lineLabel = (template: string, group: string, count: number): string => {
    const withGroup = template.replace('{{group}}', group)
    return count > 1
      ? withGroup + lineItems.groupCount.replace('{{count}}', String(count))
      : withGroup
  }

  const textLines: PricedLine[] = EMBROIDERY_LOCATIONS.flatMap(location => {
    const groupItems = itemsEnabledForBag.filter(
      item => item.typeKey === 'text' && fromPlacementId(item.placementId).location === location
    )
    if (groupItems.length === 0) return []

    return [
      {
        key: `text:${location}`,
        label: lineLabel(
          lineItems.TEXT_WITH_GROUP,
          upperFirst(location.toLowerCase()),
          groupItems.length
        ),
        inCents: totalInCentsForItems(groupItems),
      },
    ]
  })

  const logoItems = itemsEnabledForBag.filter(item => item.typeKey === 'logo')
  // Setup has its own line, so the logo line is the logo alone.
  const logoInCents = piecesInCentsForItems(logoItems)

  const iconItems = itemsEnabledForBag.filter(
    (item): item is IconItemWithIcon => item.typeKey === 'icon' && item.values.icon !== undefined
  )
  const iconLines: PricedLine[] = Object.entries(
    groupBy(iconItems, item => iconGroupLabel(item.values.icon, iconGroups))
  ).map(([group, groupItems]) => ({
    key: `icon:${group}`,
    label: lineLabel(lineItems.ICON_WITH_GROUP, group, groupItems.length),
    inCents: totalInCentsForItems(groupItems),
  }))

  return (
    <Ariakit.DisclosureProvider>
      <Container>
        <Ariakit.Disclosure render={htmlProps => <DisclosureHeader {...htmlProps} />}>
          <SummaryLabel>
            <DisclosureCarat />
            <h2>{label}</h2>
          </SummaryLabel>
          <TotalPrice aria-hidden>{formatPrice(totalInCents)}</TotalPrice>
        </Ariakit.Disclosure>
        <TotalAnnouncement role='status'>
          {totalLabel} {formatPrice(totalInCents)}
        </TotalAnnouncement>

        <Ariakit.DisclosureContent>
          <LineItems>
            {productPriceInCents !== undefined && (
              <LineItem>
                <span>{lineItems.PRODUCT}</span>
                <span>{formatPrice(productPriceInCents)}</span>
              </LineItem>
            )}
            {textLines.map(line => (
              <LineItem key={line.key}>
                <span>{line.label}</span>
                <span>{formatPrice(line.inCents)}</span>
              </LineItem>
            ))}
            {logoItems.length > 0 && (
              <LineItem>
                <span>{lineItems.LOGO}</span>
                <span>{formatPrice(logoInCents)}</span>
              </LineItem>
            )}
            {setupCount > 0 && (
              <LineItem>
                <span>{lineItems.DIGITIZATION}</span>
                <span>{formatPrice(setupInCents)}</span>
              </LineItem>
            )}
            {iconLines.map(line => (
              <LineItem key={line.key}>
                <span>{line.label}</span>
                <span>{formatPrice(line.inCents)}</span>
              </LineItem>
            ))}
          </LineItems>

          <Rule />
          <FinalSale>{finalSaleLabel}</FinalSale>
          <Total>
            <SummaryLabel>{totalLabel}</SummaryLabel>
            <TotalPrice aria-hidden>{formatPrice(totalInCents)}</TotalPrice>
          </Total>
        </Ariakit.DisclosureContent>
      </Container>
    </Ariakit.DisclosureProvider>
  )
}
